// ═══════════════════════════════════════════════════════════════
// PhoneInput.tsx – telefonszám / fax beviteli mező azonnali formátum-ellenőrzéssel
//
// - Gépelés közben jelzi, ha betű vagy nem megengedett jel kerül bele (mentés előtt).
// - A hibás mező az űrlap natív ellenőrzésében is hibás (setCustomValidity), ezért a mentés
//   gomb – az e-mail mezőhöz hasonlóan – nem küldi el az űrlapot, és a mezőre ugrik.
// - Egy korábban rögzített, régi formátumú értéket csak akkor tekint hibának, ha módosítják
//   (a szerver is csak a megváltozott értéket ellenőrzi).
// - Magyar számnál egységes alakot javasol (+36 1 234 5678), egy kattintással átvehető.
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AlertCircle, Wand2 } from 'lucide-react';
import { validatePhone, PHONE_HINT, PHONE_EXAMPLE } from '../../utils/phone';

interface Props {
  value: string | null | undefined;
  onChange: (v: string) => void;
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  /** a mező alatti súgó elrejtése (szűk helyen) – a hiba ekkor is megjelenik */
  compact?: boolean;
  'aria-label'?: string;
  /** értesítés a szülőnek (pl. ha a mentés nem űrlapon keresztül történik) */
  onValidityChange?: (ok: boolean) => void;
}

export const PhoneInput: React.FC<Props> = ({ value, onChange, id, className = '', placeholder, disabled, required, compact, onValidityChange, ...rest }) => {
  const autoId = useId();
  const inputId = id || `phone-${autoId}`;
  const msgId = `${inputId}-msg`;
  const ref = useRef<HTMLInputElement>(null);
  const initial = useRef(String(value ?? ''));
  const [touched, setTouched] = useState(false);
  const v = String(value ?? '');
  const check = useMemo(() => validatePhone(v), [v]);
  // a változatlan régi érték nem blokkol
  const blocking = !check.ok && v !== initial.current;
  const showError = !check.ok && (touched || v !== initial.current);

  useEffect(() => {
    ref.current?.setCustomValidity(blocking ? `${check.error} Formátum: ${PHONE_EXAMPLE}` : '');
    onValidityChange?.(!blocking);
  }, [blocking, check.error, onValidityChange]);

  return (
    <div>
      <input
        ref={ref}
        id={inputId}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={v}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder={placeholder || PHONE_EXAMPLE}
        disabled={disabled}
        required={required}
        aria-invalid={showError || undefined}
        aria-describedby={msgId}
        aria-label={rest['aria-label']}
        className={`${className} ${showError ? '!border-rose-400 dark:!border-rose-600 ring-1 ring-rose-300 dark:ring-rose-800' : ''}`}
      />
      <div id={msgId} className="mt-0.5 text-[10px] leading-snug" aria-live="polite">
        {showError ? (
          <span className="text-rose-600 dark:text-rose-400 flex items-start gap-1"><AlertCircle className="w-3 h-3 mt-px shrink-0" />{check.error} Formátum: {PHONE_EXAMPLE}</span>
        ) : check.warning && v !== initial.current ? (
          <span className="text-amber-700 dark:text-amber-400">{check.warning}</span>
        ) : !compact ? (
          <span className="text-slate-400">{PHONE_HINT}</span>
        ) : null}
        {check.ok && check.suggestion && !disabled && (
          <button type="button" onClick={() => onChange(check.suggestion!)}
            className="mt-0.5 flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
            title="Egységes formátum átvétele">
            <Wand2 className="w-3 h-3" /> Egységes alak: <span className="font-mono">{check.suggestion}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default PhoneInput;
